import React from 'react';
import { getIn, useFormikContext } from 'formik';

import AddressAutocomplete from '~/components/core/AddressAutocomplete/AddressAutocomplete';
import GbgAddressAutocomplete from '~/components/core/AddressAutocomplete/GbgAddressAutocomplete/GbgAddressAutocomplete';
import type { GbgRetrievedAddress } from '~/components/core/AddressAutocomplete/GbgAddressAutocomplete/types';
import {
  isGbgSupportedCountry,
  useGbgValidationStatus,
} from '~/components/core/AddressAutocomplete/GbgAddressAutocomplete/useGbgValidationStatus';
import { SUPPORTED_COUNTRIES } from '~/components/core/AddressAutocomplete/utils';
import TextFieldFormik from '~/components/TextFieldFormik';

const SUPPORTED_COUNTRIES_MINUS_GBG = SUPPORTED_COUNTRIES.filter((country) => !isGbgSupportedCountry(country));

interface AddressAutocompleteFormikProps {
  id: string;
  streetAddress1FieldId?: string;
  streetAddress2FieldId?: string;
  cityFieldId?: string;
  countyFieldId?: string;
  countryFieldId?: string;
  stateFieldId?: string;
  zipcodeFieldId?: string;
  addressValidationFieldId?: string;
  onAddressValidated?: () => void;
  className?: string;
  label: string;
  disabled?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  country?: string;
}

const AddressAutocompleteFormik: React.FC<AddressAutocompleteFormikProps> = ({
  className = '',
  id,
  label,
  helperText,
  disabled = false,
  fullWidth = false,
  streetAddress1FieldId,
  streetAddress2FieldId,
  cityFieldId,
  countyFieldId,
  countryFieldId,
  stateFieldId,
  zipcodeFieldId,
  addressValidationFieldId,
  onAddressValidated,
  country,
}) => {
  const { setFieldValue, values, setFieldTouched, errors, touched } = useFormikContext();
  const gbgValidationStatus = useGbgValidationStatus();

  const resolvedCountry = country || (countryFieldId && getIn(values, countryFieldId));
  const isGbgValidationEnabled = gbgValidationStatus.is_address_validation_enabled;
  const isDefaultAutocompleteSupported = resolvedCountry
    ? isGbgValidationEnabled
      ? SUPPORTED_COUNTRIES_MINUS_GBG.includes(resolvedCountry)
      : SUPPORTED_COUNTRIES.includes(resolvedCountry)
    : false;
  const isGbgAutocompleteSupported =
    resolvedCountry && isGbgValidationEnabled && isGbgSupportedCountry(resolvedCountry);
  const isAutocompleteSupported = isDefaultAutocompleteSupported || isGbgAutocompleteSupported;

  if (!isAutocompleteSupported) {
    return (
      <TextFieldFormik
        id={id}
        className={className}
        disabled={disabled}
        label={label}
        fullWidth={fullWidth}
        helperText={helperText}
      />
    );
  }

  const handleAddressValidated = (address: GbgRetrievedAddress) => {
    if (addressValidationFieldId) {
      setFieldValue(addressValidationFieldId, {
        provider: 'gbg',
        gbg_address_id: address.gbg_address_id,
        ...address.validation,
      });
    }
    onAddressValidated?.();
  };

  const sharedProps = {
    id,
    className,
    disabled,
    error: getIn(errors, id) && getIn(touched, id) && getIn(errors, id),
    helperText,
    label,
    value: getIn(values, id),
    onBlur: () => setFieldTouched(id, true),
    onInputChange: (_: React.SyntheticEvent | null, value: string) => setFieldValue(id, value),
    onStreetAddress1Changed: (streetAddress1: string) =>
      streetAddress1FieldId && setFieldValue(streetAddress1FieldId, streetAddress1),
    onStreetAddress2Changed: (streetAddress2: string) =>
      streetAddress2FieldId && setFieldValue(streetAddress2FieldId, streetAddress2),
    onCityChanged: (city: string) => cityFieldId && setFieldValue(cityFieldId, city),
    onStateChanged: (state: string) => stateFieldId && setFieldValue(stateFieldId, state),
    onCountyChanged: (county: string) => countyFieldId && setFieldValue(countyFieldId, county),
    onCountryChanged: (countryValue: string) => countryFieldId && setFieldValue(countryFieldId, countryValue),
    onZipcodeChanged: (zipcode: string) => zipcodeFieldId && setFieldValue(zipcodeFieldId, zipcode),
    region: resolvedCountry,
  };

  if (isGbgAutocompleteSupported) {
    return <GbgAddressAutocomplete {...sharedProps} onAddressValidated={handleAddressValidated} />;
  }

  return <AddressAutocomplete {...sharedProps} />;
};

export default AddressAutocompleteFormik;
